import * as React from "react";
import { cn } from "../../../helpers";
import styles from "./Accordion.module.scss";
import { Button } from "../Button/Button";
import ChevronDownIcon from "../../icons/ChevronDown";
import ChevronUpIcon from "../../icons/ChevronUp";

export interface AccordionProps {
    children: React.ReactNode;
    title: string;
    defaultOpen?: boolean;
    className?: string;
    titleClassName?: string;
    contentClassName?: string;
    iconPosition?: "left" | "right";
    variant?: "default" | "bordered";
    iconColor?: string;
}

export interface AccordionItemProps {
    children: React.ReactNode;
    className?: string;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
    ({ children, className }, ref) => {
        return (
            <div className={cn(styles.accordionItem, className)} ref={ref}>
                {children}
            </div>
        );
    }
);

AccordionItem.displayName = "AccordionItem";

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
    (
        {
            title,
            children,
            defaultOpen = false,
            className,
            titleClassName,
            contentClassName,
            iconPosition = "right",
            variant = "default",
            iconColor,
        },
        ref
    ) => {
        const [isOpen, setIsOpen] = React.useState(defaultOpen);

        const toggleAccordion = () => {
            setIsOpen(!isOpen);
        };

        const variantClass =
            variant === "bordered" ? styles.bordered : styles.default;

        return (
            <div className={cn(styles.accordion, variantClass, className)} ref={ref}>
                <Button
                    variant="normal"
                    size="auto"
                    onClick={toggleAccordion}
                    className={cn(styles.accordionTrigger, titleClassName)}
                >
                    {title}
                    {isOpen ? (
                        <ChevronUpIcon stroke={iconColor} />
                    ) : (
                        <ChevronDownIcon stroke={iconColor} />
                    )}
                </Button>
                <div className={cn(styles.accordionContent, isOpen && styles.open)}>
                    <div className={cn(styles.contentInner, contentClassName)}>
                        {children}
                    </div>
                </div>
            </div>
        );
    }
);

Accordion.displayName = "Accordion";

export { Accordion, AccordionItem };
